/**
 * WP-03 — the one place an API failure is turned into something a user can act on.
 *
 * ── Why this file did not exist before ─────────────────────────────────────
 *
 * None of the four axios instances has an error-normalising response
 * interceptor (`api` and `apiRoot` handle 401→refresh→retry and re-throw
 * everything else raw; `customerPortalApi` has no response interceptor at all;
 * `publicApi` is a bare `axios.create`). There was no shared extraction helper
 * either — a search for `getErrorMessage` / `extractError` / `parseApiError`
 * found zero files. So all 66 consumer files parse the error body inline, and
 * each one invented its own fallback sentence. That is why the owner sees
 * "Failed to save estimate" with nothing behind it.
 *
 * ── The bug this fixes on its own ──────────────────────────────────────────
 *
 * The dominant idiom, 67 of 114 `.data.error` reads, is:
 *
 *     message.error(err.response?.data?.error || 'Failed to save X');
 *
 * `||` does not protect that. The backend's central error handler used to put
 * an OBJECT in `data.error` — truthy — so the fallback never ran and antd
 * rendered `[object Object]`. `EstimateTemplateForm.tsx` went further and
 * called `.includes()` on it, throwing a TypeError inside a catch block on
 * every uncaught 500.
 *
 * The backend half of WP-03 makes `error` a plain string in every mode. This
 * file is the belt to that braces: `messageOf` NEVER returns a non-string, so
 * a server (or a proxy, or a cached old build) that sends something else still
 * produces a sentence rather than a crash.
 *
 * ── Three classes, treated differently ─────────────────────────────────────
 *
 *   validation / incomplete  the user can fix it — name the fields
 *   conflict                 someone or something else changed — offer reload
 *   system / offline / timeout
 *                            the user cannot fix it — say so honestly, give
 *                            the correlation id, and PROMISE THE DRAFT IS SAFE
 *
 * That last promise is load-bearing. The reason an address never got entered
 * was that entering it cost you your unsaved work; the reason a save is
 * retried five times is that nobody told the user the first failure was
 * network, not data.
 */

export type ApiErrorKind =
  | 'validation'
  | 'incomplete'
  | 'conflict'
  | 'auth'
  | 'permission'
  | 'notfound'
  | 'offline'
  | 'timeout'
  | 'system'
  | 'unknown';

export interface ApiFieldError {
  /** Path relative to the request body, e.g. `customer_id`, `customer.address`. */
  field: string;
  message: string;
  code?: string;
  /** Human label when the server supplied one. */
  label?: string;
}

export interface ParsedApiError {
  kind: ApiErrorKind;
  /** Short heading — safe to render as a title. */
  title: string;
  /** The sentence to show the user. Always a non-empty string. */
  message: string;
  /** Field-level failures, empty when there are none. */
  fields: ApiFieldError[];
  /** Present on server-side failures; quote it to support. */
  correlationId?: string;
  /** Stable machine code, when the server sent one. */
  code?: string;
  status?: number;
  /** True when the user's unsaved work is definitely still in the browser. */
  draftPreserved: boolean;
  /** True when pressing Save again, unchanged, could plausibly succeed. */
  retryable: boolean;
}

/** Anything axios might hand a catch block. */
type UnknownError = {
  response?: { status?: number; data?: unknown };
  code?: string;
  message?: string;
  request?: unknown;
} | null | undefined | unknown;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/**
 * Read a field as a STRING, or give up.
 *
 * The whole point: an object here returns `undefined`, so the caller's `??`
 * actually runs. This is the guard the 67 inline `||` sites never had.
 */
function stringField(source: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = source?.[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Pull `errors[]`, falling back to the legacy `details` / `issues` names. */
function readFields(data: Record<string, unknown> | undefined): ApiFieldError[] {
  const candidates = [data?.errors, data?.details, data?.issues];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const parsed = candidate
      .map((entry) => {
        const row = asRecord(entry);
        const field = stringField(row, 'field') ?? stringField(row, 'path');
        const message = stringField(row, 'message');
        if (!field && !message) return undefined;
        return {
          field: field ?? '',
          message: message ?? 'This value is not valid.',
          code: stringField(row, 'code'),
          label: stringField(row, 'label'),
        } as ApiFieldError;
      })
      .filter((f): f is ApiFieldError => f !== undefined);
    if (parsed.length > 0) return parsed;
  }

  // WP-02's completeness gate sends `missing: [{entity, field, label}]`. It
  // predates `errors[]` and is still emitted alongside it, so read it when the
  // generic array is absent (e.g. an older backend against a newer frontend).
  const missing = data?.missing;
  if (Array.isArray(missing)) {
    return (missing as unknown[])
      .map((entry) => {
        const row = asRecord(entry);
        const field = stringField(row, 'field');
        if (!field) return undefined;
        const label = stringField(row, 'label') ?? field;
        const entity = stringField(row, 'entity');
        return {
          field: entity ? `${entity}.${field}` : field,
          label,
          message: `${label} is required.`,
          code: 'required',
        } as ApiFieldError;
      })
      .filter((f): f is ApiFieldError => f !== undefined);
  }

  return [];
}

/** The user-visible name for a field, preferring what the server called it. */
export function fieldLabel(field: ApiFieldError): string {
  if (field.label) return field.label;
  const leaf = field.field.split('.').pop() ?? field.field;
  return leaf.replace(/_id$/, '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

function kindForStatus(status: number, code?: string): ApiErrorKind {
  if (code === 'CUSTOMER_INCOMPLETE') return 'incomplete';
  if (status === 401) return 'auth';
  if (status === 403) return 'permission';
  if (status === 404) return 'notfound';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'system';
  if (status >= 400) return 'validation';
  return 'unknown';
}

const TITLES: Record<ApiErrorKind, string> = {
  validation: 'Check these details',
  incomplete: 'More information needed',
  conflict: 'This was changed elsewhere',
  auth: 'Please sign in again',
  permission: 'Not permitted',
  notfound: 'No longer available',
  offline: "You're offline",
  timeout: 'The server did not respond',
  system: 'Something went wrong on our side',
  unknown: 'Something went wrong',
};

/**
 * Classify a caught error.
 *
 * `fallback` is the caller's own sentence, used only when the server said
 * nothing useful — not as the first choice, which is how "Failed to save
 * estimate" ended up being the only thing the owner ever saw.
 */
export function parseApiError(error: UnknownError, fallback = 'Something went wrong'): ParsedApiError {
  const err = asRecord(error);
  const response = asRecord(err?.response);
  const status = typeof response?.status === 'number' ? response.status : undefined;
  const data = asRecord(response?.data);

  // ── No response at all ───────────────────────────────────────────────────
  //
  // Distinguishing these two matters more than it looks. Both used to produce
  // "Failed to save", so the user could not tell "your data is wrong" from
  // "your phone lost signal in a customer's driveway" — and the honest answer
  // to the second is "nothing is lost, press Save again".
  if (status === undefined) {
    const axiosCode = typeof err?.code === 'string' ? err.code : undefined;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    if (offline || axiosCode === 'ERR_NETWORK') {
      return {
        kind: 'offline',
        title: TITLES.offline,
        message:
          "You're offline, so this could not be saved. Your work is still here — reconnect and press Save again.",
        fields: [],
        draftPreserved: true,
        retryable: true,
      };
    }

    if (axiosCode === 'ECONNABORTED' || axiosCode === 'ETIMEDOUT') {
      return {
        kind: 'timeout',
        title: TITLES.timeout,
        message:
          'The server took too long to answer. Your work is still here — press Save again in a moment.',
        fields: [],
        draftPreserved: true,
        retryable: true,
      };
    }

    // A request was sent and nothing came back, but the browser is online and
    // axios did not name a reason. Honest wording: we do not know whether it
    // landed, so do not claim it failed outright.
    if (err?.request) {
      return {
        kind: 'offline',
        title: 'Could not reach the server',
        message:
          'The server could not be reached. Your work is still here — check your connection and press Save again.',
        fields: [],
        draftPreserved: true,
        retryable: true,
      };
    }

    // Not an HTTP failure at all — a bug in our own code, a rejected
    // `form.validateFields()`, an abort. Do not dress it up as a server error.
    const local = stringField(err, 'message');
    return {
      kind: 'unknown',
      title: TITLES.unknown,
      message: local ?? fallback,
      fields: [],
      draftPreserved: true,
      retryable: false,
    };
  }

  const code = stringField(data, 'code');
  const kind = kindForStatus(status, code);
  const fields = readFields(data);
  const correlationId = stringField(data, 'correlation_id') ?? stringField(data, 'requestId');

  // ── The message, in order of usefulness ──────────────────────────────────
  //
  // `detail` first: it is the only member the contract defines as
  // occurrence-specific and safe to show. `error` second, and ONLY when it is
  // a string — this is the guard the inline `||` sites lacked. `title` third,
  // because it is generic but true. The caller's fallback last.
  let message =
    stringField(data, 'detail') ??
    stringField(data, 'error') ??
    stringField(data, 'message') ??
    stringField(data, 'title');

  // A 5xx `detail` is a constant plus a correlation id by contract, so there
  // is nothing field-specific to add. For everything else, if the server gave
  // us fields but no sentence, build one rather than falling back to "failed".
  if (!message && fields.length > 0) {
    message = `Check ${fields.map(fieldLabel).join(', ')}.`;
  }

  if (!message) message = fallback;

  return {
    kind,
    title: TITLES[kind],
    message,
    fields,
    correlationId,
    code,
    status,
    // The request reached the server and was refused. Nothing in the browser
    // was cleared — every save handler in this app keeps its form state on the
    // error path — so the draft is intact in all of these cases too.
    draftPreserved: true,
    // A 409 is retryable only after reloading; a 4xx needs an edit first.
    retryable: kind === 'system',
  };
}

/**
 * The message, as a string, for a caller that only wants one line.
 *
 * Drop-in replacement for `err.response?.data?.error || 'fallback'` that
 * cannot return an object and cannot throw.
 */
export function getErrorMessage(error: UnknownError, fallback = 'Something went wrong'): string {
  return parseApiError(error, fallback).message;
}

/**
 * The full text for a save failure, including the correlation id.
 *
 * A user reading "something went wrong on our side" with a reference they can
 * quote is in a different position from one reading "failed to save": the
 * first can get help, the second is stuck. That difference is the whole
 * request.
 */
export function saveErrorText(parsed: ParsedApiError): string {
  if (parsed.kind === 'system' && parsed.correlationId) {
    return `${parsed.message} If it keeps happening, quote reference ${parsed.correlationId}.`;
  }
  return parsed.message;
}

/**
 * The first field a form should focus, or `undefined`.
 *
 * Strips the entity prefix WP-02's completeness gate adds (`customer.address`),
 * because an antd `Form` names its controls by the bare column.
 */
export function firstFieldName(parsed: ParsedApiError): string | undefined {
  const first = parsed.fields.find((f) => f.field.length > 0);
  if (!first) return undefined;
  return first.field.split('.').pop();
}
